import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { combineLatest } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class DateConversionService {
  months: string[] = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  constructor(private translocoService: TranslocoService) {
    const monthKeys = [
      'shared.months.jan',
      'shared.months.feb',
      'shared.months.mar',
      'shared.months.apr',
      'shared.months.may',
      'shared.months.jun',
      'shared.months.jul',
      'shared.months.aug',
      'shared.months.sep',
      'shared.months.oct',
      'shared.months.nov',
      'shared.months.dec',
    ];

    combineLatest(monthKeys.map((key) => this.translocoService.selectTranslate(key))).subscribe((translations) => {
      this.months = translations;
    });
  }

  // Converts ISO timestamp to "DD/MM/YYYY HH:mm:ss" format
  isoToFormattedDate(isoTimestamp: string): string {
    if (!isoTimestamp) {
      return '';
    }

    const date = new Date(isoTimestamp);
    if (isNaN(date.getTime())) {
      return '';
    }

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC'
    };

    return new Intl.DateTimeFormat('en-GB', options).format(date);
  }


  // Converts a "DD/MM/YYYY HH:mm:ss" formatted date to ISO timestamp in Italian timezone
  formattedDateToIso(dateString: string): string {
    const parts = dateString.split('/');
    if (parts.length !== 3) {
      return '';
    }

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) {
      return '';
    }

    const date = new Date(Date.UTC(year, month, day, 0, 0, 0));

    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString();
  }

  // Converts ISO timestamp to formatted period e.g. Jan. 2025
  isoToFormattedPeriod(isoTimestamp: string): string {
    if (!isoTimestamp) {
      return '';
    }

    const date = new Date(isoTimestamp);
    if (isNaN(date.getTime())) {
      return '';
    }
    const month = this.months[date.getMonth()];
    const year = date.getFullYear();

    return `${month}. ${year}`;
  }

  // Converts ISO timestamp to "YYYY-MM-DD" format
  isoToSimpleDate(isoTimestamp: string): string {
    if (!isoTimestamp) {
      return '';
    }

    const date = new Date(isoTimestamp);
    if (isNaN(date.getTime())) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // Converts Date format to ISO 
  dateToIso(date: Date): string {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString();
  }

  isoToFormattedDateTime(isoTimestamp: string): string {
    if (!isoTimestamp) {
      return '';
    }

    const date = new Date(isoTimestamp);
    if (isNaN(date.getTime())) {
      return '';
    }

    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'UTC'
    };

    return new Intl.DateTimeFormat('en-GB', options).format(date);
  }

}
